import React, { FC, useState } from "react";

import { Appointment } from "../AppointmentPreviewCard/types";
import StatusDialog from "../../Unknown/StatusDialog";
import Box from "../../Unknown/Box";
import CircularProgress from "../../Unknown/CircularProgress";
import useActionButtons from "../AppointmentModifyServicesDialogContainer/useActionButtons";
import useModifyServicesTranslations from "../AppointmentModifyServicesDialogContainer/translations";
import AppointmentSummaryDetails from "../AppointmentModifyServicesDialogContainer/summary";
import useCenterServiceOptions from "../AppointmentModifyServicesDialogContainer/useCenterServiceOptions";
import SelectAddOnsForm from "../AppointmentModifyServicesDialogContainer/addOnsForm";
import useInitialSelection from "../AppointmentModifyServicesDialogContainer/getInitialSelection";
import { useSubmitHandler } from "../AppointmentModifyServicesDialogContainer/useSubmitHandler";

import ReadOnlyServices from "./readOnlyServices";
import useTranslations from "./translations";

export type AppointmentEditAddOnsDialogProps = {
  appointment: Appointment;
  isOpened: boolean;
  onClose: () => void;
  handleSuccess: () => void | Promise<void>;
};

const AppointmentEditAddOnsDialog: FC<AppointmentEditAddOnsDialogProps> = ({
  appointment,
  isOpened,
  onClose,
  handleSuccess,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { translations } = useTranslations();
  const { translations: commonTranslations } = useModifyServicesTranslations();

  const { onSubmit, isLoading } = useSubmitHandler({
    functionName: "editAppointmentAddOns",
    onSuccess: () => {
      onClose();
      handleSuccess();
    },
    onError: () => setErrorMessage(commonTranslations.errorMessage),
  });

  const onDialogClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const { initialServices, initialAddOnsIds, initialAddOnQuantities } =
    useInitialSelection(appointment);

  const optionsProps = useCenterServiceOptions({
    centerConfig: appointment.center,
    initialServices,
    initialAddOnsIds,
    initialAddOnQuantities,
    couponAmount: appointment.couponAmount,
    couponAddOnId: appointment.couponAddOnId,
  });

  const {
    isInitialized,
    summary,
    selectedCenterServices,
    selectedAddOns,
    isDirty,
  } = optionsProps;

  const actionButtons = useActionButtons({
    handleClose: onDialogClose,
    handleSubmit: () =>
      onSubmit({
        appointmentId: appointment.id || "",
        selectedAddOns,
        selectedCenterServices,
      }),
    isLoading,
    errorMessage,
    isValid: isDirty,
  });

  return (
    <StatusDialog
      open={isOpened}
      title={translations.title}
      actionButtons={actionButtons}
      errorMessage={errorMessage}
      isLoading={isLoading}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { maxWidth: 800 } }}
    >
      {!appointment.center || !optionsProps.offerings || !isInitialized ? (
        <Box
          display="flex"
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          minHeight={150}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" gap={8}>
          <Box display="flex" flexDirection="column" gap={4}>
            <ReadOnlyServices
              optionsProps={optionsProps}
              centerConfig={appointment.center}
              isSubmitting={isLoading}
            />
            <SelectAddOnsForm
              centerConfig={appointment.center}
              addOns={optionsProps.addOns}
              isSubmitting={isLoading}
              selectedAddOns={optionsProps.selectedAddOns}
              selectedCenterServices={selectedCenterServices}
              onAddOnsChange={optionsProps.setSelectedAddOns}
              isStatAvailableForAppointmentTime
              isStatLocked
            />
          </Box>
          <Box minWidth={280} maxWidth={280} flexShrink={0} top={0}>
            <AppointmentSummaryDetails
              centerConfig={appointment.center}
              selectedCenterServices={selectedCenterServices}
              selectedAddOns={optionsProps.selectedAddOns}
              discountSum={summary.discountSum}
              priceWithDiscount={summary.priceWithDiscount}
              centerAddOns={optionsProps.addOns || []}
            />
          </Box>
        </Box>
      )}
    </StatusDialog>
  );
};

export default AppointmentEditAddOnsDialog;
